import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import SyncEntryCard from './components/SyncEntryCard';
import FilterControls from './components/FilterControls';

const SyncHistory = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [syncHistory, setSyncHistory] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [filters, setFilters] = useState({
    status: 'all',
    dateRange: 'all',
    searchTerm: ''
  });

  useEffect(() => {
    if (user?.id) {
      loadSyncHistory();
    }
  }, [user]);

  useEffect(() => {
    applyFilters();
  }, [filters, syncHistory]);

  const loadSyncHistory = async () => {
    try {
      setIsLoading(true);

      // Fetch calendar connections with sync history
      const { data: connections, error: connError } = await supabase?.from('calendar_connections')?.select('*')?.eq('user_id', user?.id)?.order('last_sync_at', { ascending: false });

      if (connError) throw connError;

      // Fetch event mappings with rotation details
      const { data: mappings, error: mapError } = await supabase?.from('calendar_event_mappings')?.select(`
          *,
          parsed_rotations (
            id,
            rotation_number,
            start_date,
            end_date,
            total_credit_hours,
            status
          )
        `)?.eq('user_id', user?.id)?.order('imported_at', { ascending: false });

      if (mapError) throw mapError;

      // Group mappings by sync session (approximate by timestamp proximity)
      const syncSessions = groupBySyncSession(connections, mappings);
      setSyncHistory(syncSessions);
      setFilteredHistory(syncSessions);
    } catch (error) {
      console.error('Error loading sync history:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const groupBySyncSession = (connections, mappings) => {
    const sessions = [];

    // Create sessions from connection sync timestamps
    connections?.forEach(conn => {
      if (conn?.last_sync_at) {
        const sessionMappings = mappings?.filter(m => {
          const importTime = new Date(m?.imported_at);
          const syncTime = new Date(conn?.last_sync_at);
          const timeDiff = Math.abs(importTime - syncTime);
          return timeDiff < 5 * 60 * 1000; // Within 5 minutes
        });

        const flightEvents = sessionMappings?.filter(m => m?.rotation_id);
        const successCount = flightEvents?.filter(m => m?.parsed_rotations?.status !== 'error')?.length;
        const errorCount = flightEvents?.length - successCount;

        sessions?.push({
          id: `sync-${conn?.id}-${conn?.last_sync_at}`,
          timestamp: conn?.last_sync_at,
          provider: conn?.provider,
          totalEvents: sessionMappings?.length || 0,
          flightEvents: flightEvents?.length || 0,
          newEvents: flightEvents?.length || 0,
          changedEvents: 0,
          status: errorCount > 0 ? 'error' : 'success',
          successCount,
          errorCount,
          rotations: flightEvents?.map(m => m?.parsed_rotations)?.filter(Boolean),
          syncFrequency: conn?.sync_frequency,
          autoSync: conn?.auto_sync_enabled
        });
      }
    });

    return sessions;
  };

  const applyFilters = () => {
    let filtered = [...syncHistory];

    // Status filter
    if (filters?.status !== 'all') {
      filtered = filtered?.filter(s => s?.status === filters?.status);
    }

    // Date range filter
    if (filters?.dateRange !== 'all') {
      const now = new Date();
      const ranges = {
        today: 1,
        week: 7,
        month: 30
      };
      const days = ranges?.[filters?.dateRange];
      if (days) {
        const cutoff = new Date(now - days * 24 * 60 * 60 * 1000);
        filtered = filtered?.filter(s => new Date(s?.timestamp) >= cutoff);
      }
    }

    // Search term
    if (filters?.searchTerm) {
      const term = filters?.searchTerm?.toLowerCase();
      filtered = filtered?.filter(s => 
        s?.rotations?.some(r => 
          r?.rotation_number?.toLowerCase()?.includes(term)
        )
      );
    }

    setFilteredHistory(filtered);
  };

  const handleExport = () => {
    const exportData = filteredHistory?.map(s => ({
      timestamp: s?.timestamp,
      status: s?.status,
      totalEvents: s?.totalEvents,
      flightEvents: s?.flightEvents,
      successCount: s?.successCount,
      errorCount: s?.errorCount,
      rotations: s?.rotations?.map(r => r?.rotation_number)?.join(', ')
    }));

    const csv = [
      ['Timestamp', 'Status', 'Total Events', 'Flight Events', 'Success', 'Errors', 'Rotations'],
      ...exportData?.map(row => [
        row?.timestamp,
        row?.status,
        row?.totalEvents,
        row?.flightEvents,
        row?.successCount,
        row?.errorCount,
        row?.rotations
      ])
    ]?.map(row => row?.join(','))?.join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sync-history-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    a?.click();
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />
      <main className="flex-1 lg:ml-60 pb-16 lg:pb-0">
        <div className="p-4 md:p-6 lg:p-8 space-y-6">
          <Breadcrumb items={[
            { label: 'Dashboard', href: '/dashboard' },
            { label: 'Settings', href: '/settings' },
            { label: 'Sync History', href: '/sync-history' }
          ]} />

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-foreground">Sync History</h1>
              <p className="text-sm md:text-base text-muted-foreground mt-1 font-caption">
                Complete visibility into calendar import operations and rotation parsing
              </p>
            </div>
            <Button
              onClick={handleExport}
              variant="outline"
              className="flex items-center gap-2"
              disabled={filteredHistory?.length === 0}
            >
              <Icon name="Download" size={16} />
              Export
            </Button>
          </div>

          <FilterControls
            filters={filters}
            onFilterChange={setFilters}
          />

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-blue"></div>
            </div>
          ) : filteredHistory?.length === 0 ? (
            <div className="card text-center py-12">
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-accent-cyan/20">
                <Icon name="Calendar" size={32} color="var(--color-accent-cyan)" />
              </div>
              <h3 className="text-xl font-bold text-foreground mb-2">No Sync History</h3>
              <p className="text-muted-foreground font-caption mb-6">
                {filters?.status !== 'all' || filters?.dateRange !== 'all' || filters?.searchTerm ?'No sync events match your filters' :'Connect your calendar to start syncing flight schedules'}
              </p>
              <Button
                onClick={() => navigate('/settings')}
                className="mx-auto"
              >
                Go to Settings
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground font-caption">
                  Showing {filteredHistory?.length} sync {filteredHistory?.length === 1 ? 'event' : 'events'}
                </p>
              </div>

              <div className="space-y-4">
                {filteredHistory?.map(sync => (
                  <SyncEntryCard
                    key={sync?.id}
                    sync={sync}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default SyncHistory;